/**
 * Som curto de notificação gerado via Web Audio API (sem arquivo MP3).
 * Toca um "bing" simples de 2 notas. Opt-in: só toca se o usuário tiver
 * habilitado via `setSoundEnabled(true)`.
 */

const STORAGE_KEY = 'fismatek-notif-sound';

export function isSoundEnabled(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setSoundEnabled(enabled: boolean) {
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    /* ignora */
  }
}

let cachedCtx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return null;
  if (!cachedCtx) cachedCtx = new Ctx();
  return cachedCtx;
}

/**
 * Toca um beep curto de 2 tons (C5 + E5) com decay.
 * Chame ao receber evento se o usuário habilitou o som.
 * Devolve uma Promise que resolve quando o som termina.
 */
export async function playNotificationSound(): Promise<void> {
  if (!isSoundEnabled()) return;
  const ctx = getCtx();
  if (!ctx) return;

  // Alguns navegadores suspendem o AudioContext até a primeira interação do usuário
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      return;
    }
  }

  const now = ctx.currentTime;
  const playTone = (freq: number, start: number, duration = 0.18) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.18, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + duration);
  };
  playTone(880, now); // A5
  playTone(1320, now + 0.12, 0.22); // E6

  await new Promise<void>((resolve) => setTimeout(resolve, 350));
}
